import * as StellarSdk from '@stellar/stellar-sdk';
import { FeeBumpTransaction, Memo } from '@stellar/stellar-sdk';
import type { MemoType } from '@stellar/stellar-sdk';

import { ENV_TESTNET } from 'constants/env';
import { BASE_FEE } from 'constants/stellar';
import { DELEGATE_MARKER_KEY, MARKET_KEY_MARKER_UP } from 'constants/stellar-accounts';

import { getAssetFromString } from 'helpers/assets';
import { getEnv, getNetworkPassphrase } from 'helpers/env';
import { getTxSentryExtra } from 'helpers/sentry';
import { retryTransactionSubmit } from 'helpers/transaction-retry';

import AccountService from 'services/account.service';
import EventService from 'services/event.service';
import SentryService from 'services/sentry.service';
import Account from 'services/stellar/account/account';
import { StellarEvents, StellarPayload } from 'services/stellar/events/events';
import Horizon from 'services/stellar/horizon/horizon';
import Operation from 'services/stellar/operation/operation';

import { ClassicToken, Token } from 'types/token';

export default class Transaction {
    private readonly horizon: Horizon;
    private readonly account: Account;
    private readonly event: EventService<StellarEvents, StellarPayload>;
    private readonly op: Operation;

    constructor(
        horizon: Horizon,
        account: Account,
        event: EventService<StellarEvents, StellarPayload>,
        op: Operation,
    ) {
        this.horizon = horizon;
        this.account = account;
        this.event = event;
        this.op = op;
    }

    async buildTx(
        account: StellarSdk.TransactionSource,
        operations: StellarSdk.xdr.Operation | StellarSdk.xdr.Operation[],
        memo?: StellarSdk.Memo,
    ) {
        const newAccount = await this.account.loadAccount(account.accountId());

        this.event.trigger({ type: StellarEvents.handleAccountUpdate, account: newAccount });

        const tx = new StellarSdk.TransactionBuilder(newAccount, {
            fee: BASE_FEE,
            networkPassphrase: getNetworkPassphrase(),
        }).setTimeout(StellarSdk.TimeoutInfinite);

        if (Array.isArray(operations)) {
            operations.forEach(op => {
                tx.addOperation(op);
            });
        } else {
            tx.addOperation(operations);
        }

        if (memo) {
            tx.addMemo(memo);
        }

        return tx.build();
    }

    buildTxFromXdr(xdr: string): StellarSdk.Transaction | FeeBumpTransaction {
        return StellarSdk.TransactionBuilder.fromXDR(xdr, getNetworkPassphrase());
    }

    createMemo(type: MemoType, value): Memo {
        return new StellarSdk.Memo(type, value);
    }

    async submitTx(tx: StellarSdk.Transaction) {
        try {
            return await retryTransactionSubmit(() => this.horizon.server.submitTransaction(tx));
        } catch (error) {
            SentryService.captureException({
                error,
                extra: {
                    ...getTxSentryExtra(tx, error),
                    response: (error as { response?: { data?: unknown } })?.response?.data,
                },
                prefix: 'Stellar transaction submit failed',
            });

            throw error;
        }
    }

    async createMarketKeyTx(
        sourceAccountId: string,
        asset1: Token,
        asset2: Token,
        totalAmount: number,
    ) {
        const updatedAccount = await this.account.loadAccount(sourceAccountId);
        const marketKeyUp = StellarSdk.Keypair.random();

        const transactionBuilder = new StellarSdk.TransactionBuilder(updatedAccount, {
            fee: BASE_FEE,
            networkPassphrase: getNetworkPassphrase(),
        });

        this.op.getCreateMarketKeyOps(
            transactionBuilder,
            marketKeyUp.publicKey(),
            asset1,
            asset2,
            totalAmount,
            MARKET_KEY_MARKER_UP,
        );

        transactionBuilder.setTimeout(StellarSdk.TimeoutInfinite);

        const transaction = transactionBuilder.build();

        transaction.sign(marketKeyUp);

        return transaction;
    }

    createDelegateTx(
        account: AccountService,
        delegateDestination: string,
        delegations: Array<{ token: ClassicToken; amount: string }>,
    ) {
        return this.buildTx(
            account,
            delegations.map(({ token, amount }) =>
                StellarSdk.Operation.createClaimableBalance({
                    source: account.accountId(),
                    amount: amount.toString(),
                    asset: token,
                    claimants: [
                        new StellarSdk.Claimant(
                            account.accountId(),
                            StellarSdk.Claimant.predicateNot(
                                StellarSdk.Claimant.predicateBeforeAbsoluteTime(
                                    ((Date.now() + 25 * 60 * 60 * 1000) / 1000).toFixed(), // 25 hours
                                ),
                            ),
                        ),
                        new StellarSdk.Claimant(
                            delegateDestination,
                            StellarSdk.Claimant.predicateNot(
                                StellarSdk.Claimant.predicateUnconditional(),
                            ),
                        ),
                        new StellarSdk.Claimant(
                            DELEGATE_MARKER_KEY,
                            StellarSdk.Claimant.predicateNot(
                                StellarSdk.Claimant.predicateUnconditional(),
                            ),
                        ),
                    ],
                }),
            ),
        );
    }

    async createTestnetAssetsDistributeTx(
        account: AccountService,
    ): Promise<StellarSdk.Transaction> {
        if (getEnv() !== ENV_TESTNET) {
            throw new Error('Testnet faucet is testnet-only');
        }

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        const TESTNET_ASSETS_ISSUER_SECRET = process.variable.TESTNET_ASSETS_ISSUER_SECRET;

        const issuerKp = StellarSdk.Keypair.fromSecret(TESTNET_ASSETS_ISSUER_SECRET);
        const issuerPub = issuerKp.publicKey();

        const operations: StellarSdk.xdr.Operation[] = [];
        const missingAssets = account.getMissingTestnetDistributionAssets();

        if (!missingAssets.length) {
            throw new Error('All configured testnet assets are already available');
        }

        missingAssets.forEach(([assetString, amount]) => {
            const asset = getAssetFromString(assetString) as ClassicToken;
            const hasTrustline = account.getAssetBalance(asset) !== null;

            if (!hasTrustline) {
                operations.push(
                    StellarSdk.Operation.changeTrust({
                        asset,
                    }),
                );
            }

            operations.push(
                StellarSdk.Operation.payment({
                    source: issuerPub,
                    destination: account.accountId(),
                    asset,
                    amount,
                }),
            );
        });

        const tx = await this.buildTx(account, operations);
        tx.sign(issuerKp);
        return tx;
    }
}
